import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-lg px-3 py-2 text-sm outline-none transition-colors";

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", editor: "Editor" };
const ROLE_STYLES = {
  super_admin: "bg-[#9A7B66] text-white",
  admin: "bg-[#EFEAE1] text-[#9A7B66] border border-[#9A7B66]/25",
  editor: "bg-gray-100 text-gray-600",
};

export default function UsersTab({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [resetPw, setResetPw] = useState({});

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setShowForm(false);
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const resetPassword = async (id) => {
    const pw = (resetPw[id] || "").trim();
    if (!pw) return;
    try {
      await api.put(`/admin/users/${id}`, { password: pw });
      toast.success("Password updated");
      setResetPw({ ...resetPw, [id]: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-tab" className="max-w-3xl">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-2xl text-[#2C2825]">Users & Roles</h2>
        <button
          data-testid="users-add-button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#9A7B66] hover:bg-[#826552] text-white text-xs font-semibold uppercase tracking-wider transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" /> Create User
        </button>
      </div>
      <p className="text-sm text-[#A0988E] mb-6">
        Only Super Admin can see this tab and create users. Super Admin has access to everything. Admin manages all
        content, settings and inquiries. Editor manages content only (hero, villa, experiences, gallery, blog,
        reviews).
      </p>

      {showForm && (
        <div data-testid="users-form" className="mb-8 bg-white border border-[#E5DED4] rounded-md p-6 space-y-4">
          <h3 className="font-serif text-lg text-[#2C2825]">Create User</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">Name</label>
              <input data-testid="users-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">Email</label>
              <input data-testid="users-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">Password</label>
              <input data-testid="users-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">Role</label>
              <select data-testid="users-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                {Object.entries(ROLE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button data-testid="users-save-button" onClick={create} className="px-6 py-2.5 rounded-full bg-[#9A7B66] hover:bg-[#826552] text-white text-xs font-semibold uppercase tracking-wider transition-colors">
              Save
            </button>
            <button data-testid="users-cancel-button" onClick={() => setShowForm(false)} className="px-6 py-2.5 rounded-full border border-[#D3C8B9] text-[#6E675F] text-xs font-semibold uppercase tracking-wider hover:bg-[#F4EFEA] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!users ? (
        <p className="text-sm text-[#A0988E]">Loading...</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white border border-[#E5DED4] rounded-xl p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="font-sans font-semibold text-sm text-[#2C2825]">
                  {u.name}
                  {u.id === currentUser?.id && <span className="ml-2 text-[10px] text-[#A0988E]">(you)</span>}
                </div>
                <div className="text-xs text-[#A0988E]">{u.email}</div>
              </div>
              <select
                data-testid={`user-role-select-${u.id}`}
                value={u.role}
                onChange={(e) => changeRole(u.id, e.target.value)}
                className={`text-[11px] font-semibold uppercase tracking-wider rounded-full px-3 py-1.5 outline-none cursor-pointer ${ROLE_STYLES[u.role] || ROLE_STYLES.editor}`}
              >
                {Object.entries(ROLE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`user-resetpw-input-${u.id}`}
                  type="password"
                  placeholder="New password"
                  value={resetPw[u.id] || ""}
                  onChange={(e) => setResetPw({ ...resetPw, [u.id]: e.target.value })}
                  className="w-32 bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-lg px-2.5 py-1.5 text-xs outline-none transition-colors"
                />
                <button
                  data-testid={`user-resetpw-button-${u.id}`}
                  onClick={() => resetPassword(u.id)}
                  className="px-3 py-1.5 rounded-lg bg-[#EFEAE1] border border-[#9A7B66]/25 text-[#9A7B66] text-[11px] font-semibold hover:bg-[#9A7B66] hover:text-white transition-colors"
                >
                  Reset
                </button>
                <button
                  data-testid={`user-delete-${u.id}`}
                  onClick={() => remove(u.id)}
                  className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
